// ── Form Builder Types ──

export interface FormBlockValidation {
  minLength?: number
  maxLength?: number
  min?: number
  max?: number
  pattern?: string
  accept?: string // for file inputs
  maxFileSize?: number // in MB
  options?: { label: string; value: string }[] // for select, radio, checkbox
}

export interface FormBlock {
  id: string
  type: FormBlockType
  label?: string
  name: string
  placeholder?: string
  required?: boolean
  helpText?: string
  defaultValue?: string
  width?: 'full' | 'half'
  validation: {
    minLength?: number
    maxLength?: number
    min?: number
    max?: number
    pattern?: string
    options?: { label: string; value: string }[]
    accept?: string
    maxFileSize?: number
  }
  // Presentation
  content?: string // for headings, paragraphs
  level?: 1 | 2 | 3 | 4 | 5 | 6 // for headings
  height?: number // for spacers
  src?: string // for image
  alt?: string // for image
  // Image layout & effects
  imageAlign?: 'left' | 'center' | 'right'
  imageSizing?: 'natural' | 'maxWidth' | 'custom'
  imageMaxWidth?: number
  imageWidth?: number
  imageHeight?: number
  imageMaxHeight?: number
  imageObjectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'
  imagePosition?: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky'
  imageAnimation?:
    | 'none'
    | 'fadeIn'
    | 'slideUp'
    | 'slideDown'
    | 'zoomIn'
    | 'blurIn'
  imageBorderRadius?: number
  imageOpacity?: number
  imageShadow?: 'none' | 'sm' | 'md' | 'lg'
  imageTopOffset?: number
  imageLeftOffset?: number
  // Submit button
  buttonText?: string
  buttonAlign?: 'left' | 'center' | 'right' | 'full'
  buttonColor?: string
  buttonPosition?: 'static' | 'sticky' | 'fixed'
  buttonStickyOffset?: number
  // Consent-specific
  consentText?: string
  // Section-specific
  sectionTitle?: string
  sectionShowTitle?: boolean
  sectionAnimation?:
    | 'none'
    | 'slideLeft'
    | 'slideRight'
    | 'slideUp'
    | 'slideDown'
    | 'fadeIn'
    | 'zoomIn'
  sectionCollapsible?: boolean
  sectionBorderStyle?: 'none' | 'solid' | 'dashed' | 'accent'
  sectionBackground?: string
  children?: FormBlock[]
  // Columns-specific
  columns?: FormBlock[][]
  columnCount?: number // 2-6, default 2
  columnGap?: number // gap in px, default 16
  // Subform-specific
  subformName?: string
  subformShowName?: boolean
  subformColumns?: {
    label: string
    type: string
    options?: { label: string; value: string }[]
    required?: boolean
    placeholder?: string
  }[]
  subformMinRows?: number
  subformMaxRows?: number
  // Video embed
  videoUrl?: string
  videoAspectRatio?: '16:9' | '4:3' | '1:1' | '9:16'
  videoAutoplay?: boolean
  videoMuted?: boolean
  videoLoop?: boolean
  // Phone-specific
  phoneDefaultCountry?: string
  // Radio/Checkbox layout
  optionsLayout?: 'vertical' | 'horizontal'
  optionsColumns?: 1 | 2 | 3 | 4
  // reCAPTCHA-specific
  recaptchaSiteKey?: string
  recaptchaTheme?: 'light' | 'dark'
  recaptchaSize?: 'normal' | 'compact'
}

export type FormBlockType =
  | 'text'
  | 'email'
  | 'phone'
  | 'number'
  | 'textarea'
  | 'url'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'consent'
  | 'date'
  | 'file'
  | 'hidden'
  | 'heading'
  | 'paragraph'
  | 'divider'
  | 'spacer'
  | 'image'
  | 'submit'
  | 'section'
  | 'columns'
  | 'subform'
  | 'video_embed'
  | 'recaptcha'

export interface FormTheme {
  fontFamily: string
  primaryColor: string
  borderRadius: number
  backgroundColor: string
  labelColor: string
  inputBorderColor: string
  buttonStyle: 'filled' | 'outline'
  // Enhanced styling
  backgroundType: 'solid' | 'gradient'
  gradientFrom: string
  gradientTo: string
  gradientDirection:
    | 'to bottom'
    | 'to right'
    | 'to bottom right'
    | 'to top right'
  labelFontSize: number
  labelFontWeight: '400' | '500' | '600' | '700'
  inputBackground: string
  inputFocusColor: string
  inputShadow: boolean
  formPadding: number
  fieldSpacing: number
  buttonTextColor: string
  buttonShadow: boolean
  containerShadow: 'none' | 'sm' | 'md' | 'lg'
  containerBorder: boolean
  headingColor: string
  paragraphColor: string
  layoutMode: 'standard' | 'card'
  // Card mode settings
  cardPrevLabel: string
  cardNextLabel: string
  cardNavStyle: 'text' | 'icons' | 'pills'
  cardShowProgress: boolean
  cardShowCounter: boolean
  cardTransition: 'fade' | 'slide' | 'none'
  cardWidth: number
}

export const DEFAULT_FORM_THEME: FormTheme = {
  fontFamily: 'Inter, system-ui, sans-serif',
  primaryColor: '#2563eb',
  borderRadius: 6,
  backgroundColor: '#ffffff',
  labelColor: '#374151',
  inputBorderColor: '#d1d5db',
  buttonStyle: 'filled',
  backgroundType: 'solid',
  gradientFrom: '#667eea',
  gradientTo: '#764ba2',
  gradientDirection: 'to bottom right',
  labelFontSize: 14,
  labelFontWeight: '500',
  inputBackground: '#ffffff',
  inputFocusColor: '#2563eb',
  inputShadow: false,
  formPadding: 32,
  fieldSpacing: 20,
  buttonTextColor: '#ffffff',
  buttonShadow: false,
  containerShadow: 'none',
  containerBorder: false,
  headingColor: '#111827',
  paragraphColor: '#6b7280',
  layoutMode: 'standard',
  cardPrevLabel: '← Back',
  cardNextLabel: 'Next →',
  cardNavStyle: 'text',
  cardShowProgress: true,
  cardShowCounter: true,
  cardTransition: 'fade',
  cardWidth: 720,
}

export interface StylePreset {
  name: string
  emoji: string
  theme: Partial<FormTheme>
}

// ── Logic Engine Types ──

export type LogicConditionType =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'is_empty'
  | 'is_not_empty'
  | 'greater_than'
  | 'less_than'
  | 'one_of'

export type LogicActionType =
  | 'show_block'
  | 'hide_block'
  | 'require_block'
  | 'optional_block'
  | 'set_field_value'
  | 'jump_to_block'
  | 'show_section'
  | 'hide_section'
  | 'filter_options'

export interface LogicCondition {
  id: string
  fieldId: string // The ID of the block triggering the rule
  operator: LogicConditionType
  value: string | string[]
}

export interface LogicAction {
  id: string
  type: LogicActionType
  targetId: string // The ID of the block to affect
  value?: string // For set_field_value
  filterValues?: string[] // For filter_options — option values to SHOW
}

export interface FormLogicRule {
  id: string
  name: string
  conditions: LogicCondition[]
  conditionMatch: 'all' | 'any' // Match ALL conditions or ANY condition
  actions: LogicAction[]
}

export const FORM_STYLE_PRESETS: StylePreset[] = [
  {
    name: 'Clean Default',
    emoji: '✨',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#2563eb',
      borderRadius: 6,
      backgroundColor: '#ffffff',
      backgroundType: 'solid',
      labelColor: '#374151',
      inputBorderColor: '#d1d5db',
      inputBackground: '#ffffff',
      inputShadow: false,
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: false,
      containerShadow: 'none',
      containerBorder: false,
      headingColor: '#111827',
      paragraphColor: '#6b7280',
      formPadding: 32,
      fieldSpacing: 20,
    },
  },
  {
    name: 'Soft Gradient',
    emoji: '🌈',
    theme: {
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      primaryColor: '#7c3aed',
      borderRadius: 12,
      backgroundColor: '#ffffff',
      backgroundType: 'gradient',
      gradientFrom: '#e0c3fc',
      gradientTo: '#8ec5fc',
      gradientDirection: 'to bottom right',
      labelColor: '#4c1d95',
      inputBorderColor: '#c4b5fd',
      inputBackground: 'rgba(255,255,255,0.85)',
      inputShadow: true,
      inputFocusColor: '#7c3aed',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'lg',
      containerBorder: false,
      headingColor: '#4c1d95',
      paragraphColor: '#6d28d9',
      formPadding: 40,
      fieldSpacing: 24,
    },
  },
  {
    name: 'Dark Mode',
    emoji: '🌙',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#6366f1',
      borderRadius: 8,
      backgroundColor: '#1e1e2e',
      backgroundType: 'solid',
      labelColor: '#cbd5e1',
      inputBorderColor: '#334155',
      inputBackground: '#0f172a',
      inputShadow: false,
      inputFocusColor: '#818cf8',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'lg',
      containerBorder: true,
      headingColor: '#f1f5f9',
      paragraphColor: '#94a3b8',
      formPadding: 36,
      fieldSpacing: 22,
    },
  },
  {
    name: 'Warm & Friendly',
    emoji: '☀️',
    theme: {
      fontFamily: 'Georgia, serif',
      primaryColor: '#ea580c',
      borderRadius: 10,
      backgroundColor: '#fffbeb',
      backgroundType: 'solid',
      labelColor: '#92400e',
      inputBorderColor: '#fbbf24',
      inputBackground: '#ffffff',
      inputShadow: true,
      inputFocusColor: '#ea580c',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'md',
      containerBorder: false,
      headingColor: '#78350f',
      paragraphColor: '#a16207',
      formPadding: 36,
      fieldSpacing: 22,
    },
  },
  {
    name: 'Minimal Outline',
    emoji: '🔲',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#111827',
      borderRadius: 0,
      backgroundColor: '#ffffff',
      backgroundType: 'solid',
      labelColor: '#111827',
      labelFontWeight: '600',
      inputBorderColor: '#111827',
      inputBackground: '#ffffff',
      inputShadow: false,
      inputFocusColor: '#111827',
      buttonStyle: 'outline',
      buttonTextColor: '#111827',
      buttonShadow: false,
      containerShadow: 'none',
      containerBorder: true,
      headingColor: '#111827',
      paragraphColor: '#4b5563',
      formPadding: 32,
      fieldSpacing: 20,
    },
  },
  {
    name: 'Ocean Breeze',
    emoji: '🌊',
    theme: {
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      primaryColor: '#0891b2',
      borderRadius: 12,
      backgroundColor: '#ffffff',
      backgroundType: 'gradient',
      gradientFrom: '#cffafe',
      gradientTo: '#a5f3fc',
      gradientDirection: 'to bottom',
      labelColor: '#164e63',
      inputBorderColor: '#67e8f9',
      inputBackground: 'rgba(255,255,255,0.9)',
      inputShadow: true,
      inputFocusColor: '#0891b2',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'md',
      containerBorder: false,
      headingColor: '#155e75',
      paragraphColor: '#0e7490',
      formPadding: 40,
      fieldSpacing: 24,
    },
  },
  {
    name: 'Corporate Blue',
    emoji: '🏢',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#1e40af',
      borderRadius: 4,
      backgroundColor: '#f8fafc',
      backgroundType: 'solid',
      labelColor: '#1e293b',
      labelFontWeight: '600',
      inputBorderColor: '#cbd5e1',
      inputBackground: '#ffffff',
      inputShadow: false,
      inputFocusColor: '#1e40af',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: false,
      containerShadow: 'sm',
      containerBorder: true,
      headingColor: '#0f172a',
      paragraphColor: '#475569',
      formPadding: 32,
      fieldSpacing: 20,
    },
  },
  {
    name: 'Rose Gold',
    emoji: '🌹',
    theme: {
      fontFamily: 'Georgia, serif',
      primaryColor: '#be185d',
      borderRadius: 16,
      backgroundColor: '#fff1f2',
      backgroundType: 'solid',
      labelColor: '#9f1239',
      labelFontWeight: '500',
      inputBorderColor: '#fda4af',
      inputBackground: '#ffffff',
      inputShadow: true,
      inputFocusColor: '#be185d',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'md',
      containerBorder: false,
      headingColor: '#881337',
      paragraphColor: '#be185d',
      formPadding: 40,
      fieldSpacing: 24,
    },
  },
  {
    name: 'Forest Green',
    emoji: '🌲',
    theme: {
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      primaryColor: '#15803d',
      borderRadius: 8,
      backgroundColor: '#f0fdf4',
      backgroundType: 'solid',
      labelColor: '#14532d',
      labelFontWeight: '500',
      inputBorderColor: '#86efac',
      inputBackground: '#ffffff',
      inputShadow: false,
      inputFocusColor: '#15803d',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'sm',
      containerBorder: true,
      headingColor: '#14532d',
      paragraphColor: '#166534',
      formPadding: 36,
      fieldSpacing: 22,
    },
  },
  {
    name: 'Neon Pop',
    emoji: '⚡',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#a855f7',
      borderRadius: 8,
      backgroundColor: '#0a0a0a',
      backgroundType: 'solid',
      labelColor: '#e2e8f0',
      labelFontWeight: '600',
      inputBorderColor: '#7c3aed',
      inputBackground: '#18181b',
      inputShadow: true,
      inputFocusColor: '#a855f7',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'lg',
      containerBorder: true,
      headingColor: '#f0abfc',
      paragraphColor: '#c084fc',
      formPadding: 36,
      fieldSpacing: 22,
    },
  },
  {
    name: 'Pastel Dream',
    emoji: '🍬',
    theme: {
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      primaryColor: '#8b5cf6',
      borderRadius: 20,
      backgroundColor: '#faf5ff',
      backgroundType: 'gradient',
      gradientFrom: '#fce7f3',
      gradientTo: '#ede9fe',
      gradientDirection: 'to bottom right',
      labelColor: '#6b21a8',
      labelFontWeight: '500',
      inputBorderColor: '#d8b4fe',
      inputBackground: 'rgba(255,255,255,0.8)',
      inputShadow: true,
      inputFocusColor: '#8b5cf6',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'md',
      containerBorder: false,
      headingColor: '#581c87',
      paragraphColor: '#7e22ce',
      formPadding: 40,
      fieldSpacing: 24,
    },
  },
  {
    name: 'Slate Professional',
    emoji: '💼',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#475569',
      borderRadius: 4,
      backgroundColor: '#ffffff',
      backgroundType: 'solid',
      labelColor: '#334155',
      labelFontWeight: '600',
      inputBorderColor: '#94a3b8',
      inputBackground: '#f8fafc',
      inputShadow: false,
      inputFocusColor: '#475569',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: false,
      containerShadow: 'sm',
      containerBorder: true,
      headingColor: '#0f172a',
      paragraphColor: '#64748b',
      formPadding: 32,
      fieldSpacing: 18,
    },
  },
  {
    name: 'Sunset Vibes',
    emoji: '🌅',
    theme: {
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      primaryColor: '#ea580c',
      borderRadius: 12,
      backgroundColor: '#ffffff',
      backgroundType: 'gradient',
      gradientFrom: '#fed7aa',
      gradientTo: '#fecaca',
      gradientDirection: 'to bottom right',
      labelColor: '#9a3412',
      labelFontWeight: '500',
      inputBorderColor: '#fdba74',
      inputBackground: 'rgba(255,255,255,0.9)',
      inputShadow: true,
      inputFocusColor: '#ea580c',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'lg',
      containerBorder: false,
      headingColor: '#7c2d12',
      paragraphColor: '#c2410c',
      formPadding: 40,
      fieldSpacing: 24,
    },
  },
  {
    name: 'Glassmorphism',
    emoji: '💎',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#3b82f6',
      borderRadius: 16,
      backgroundColor: 'rgba(255,255,255,0.25)',
      backgroundType: 'gradient',
      gradientFrom: 'rgba(255,255,255,0.4)',
      gradientTo: 'rgba(255,255,255,0.1)',
      gradientDirection: 'to bottom right',
      labelColor: '#1e293b',
      labelFontWeight: '500',
      inputBorderColor: 'rgba(255,255,255,0.5)',
      inputBackground: 'rgba(255,255,255,0.6)',
      inputShadow: true,
      inputFocusColor: '#3b82f6',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'lg',
      containerBorder: true,
      headingColor: '#0f172a',
      paragraphColor: '#334155',
      formPadding: 36,
      fieldSpacing: 22,
    },
  },
]

export interface FormBuilderSchema {
  blocks: FormBlock[]
  theme: FormTheme
  rules?: FormLogicRule[]
}

// ── Email Builder Types ──

export type EmailBlockType =
  | 'text'
  | 'heading'
  | 'button'
  | 'image'
  | 'video_thumbnail'
  | 'divider'
  | 'spacer'
  | 'columns'
  | 'social'
  | 'unsubscribe'
  | 'image_text'
  | 'footer'
  | 'table'
  | 'boxed_text'
  | 'html_block'

export interface EmailBlock {
  id: string
  type: EmailBlockType
  // Text / Heading
  content?: string
  fontSize?: number
  fontWeight?: string
  color?: string
  align?: 'left' | 'center' | 'right'
  lineHeight?: number
  // Button
  buttonText?: string
  buttonUrl?: string
  buttonBgColor?: string
  buttonTextColor?: string
  buttonBorderRadius?: number
  buttonWidth?: 'auto' | 'full'
  // Image / Video
  src?: string
  alt?: string
  imageWidth?: number // percentage
  href?: string
  // Enhanced image properties (matching FormBuilder)
  imageAlign?: 'left' | 'center' | 'right'
  imageBorderRadius?: number
  imageOpacity?: number
  imageShadow?: 'none' | 'sm' | 'md' | 'lg'
  imageObjectFit?: string
  imageSizing?: 'natural' | 'maxWidth' | 'custom'
  imageMaxWidth?: number
  imageHeight?: number
  imageMaxHeight?: number
  // Video thumbnail
  videoUrl?: string
  // Divider
  dividerColor?: string
  dividerThickness?: number
  dividerStyle?: 'solid' | 'dashed' | 'dotted'
  // Spacer
  height?: number
  // Columns
  columns?: EmailBlock[][]
  columnRatio?: '50-50' | '33-67' | '67-33' | '33-33-33'
  // Social
  socialLinks?: { platform: string; url: string }[]
  socialIconSize?: number
  // Shared
  paddingTop?: number
  paddingBottom?: number
  paddingLeft?: number
  paddingRight?: number
  backgroundColor?: string
  // Image+Text
  imageTextLayout?: 'image-left' | 'image-right'
  imageTextImageSrc?: string
  imageTextImageAlt?: string
  imageTextContent?: string
  imageTextImageWidth?: number
  // Footer
  footerCompanyName?: string
  footerAddress?: string
  footerLinks?: { label: string; url: string }[]
  // Table
  tableRows?: number
  tableColumns?: number
  tableData?: {
    rows: string[][]
    hasHeader: boolean
    headerBgColor: string
    headerTextColor: string
    borderColor: string
    stripedRows: boolean
    stripedColor: string
    cellAlign: 'left' | 'center' | 'right'
  }
  // Boxed Text
  boxedTextBorderColor?: string
  boxedTextBorderRadius?: number
  boxedTextBgColor?: string
  // HTML Block
  htmlContent?: string
}

export interface EmailBuilderSchema {
  blocks: EmailBlock[]
  globalStyles: EmailGlobalStyles
}

export interface EmailGlobalStyles {
  backgroundColor: string
  innerColor: string
  contentWidth: number
  fontFamily: string
  textColor: string
  linkColor: string
  outerBorderSize: number
  outerBorderColor: string
  rtlEnabled: boolean
}

export const DEFAULT_EMAIL_STYLES: EmailGlobalStyles = {
  backgroundColor: '#f3f4f6',
  innerColor: '#ffffff',
  contentWidth: 600,
  fontFamily: 'Arial, Helvetica, sans-serif',
  textColor: '#333333',
  linkColor: '#2563eb',
  outerBorderSize: 0,
  outerBorderColor: '#000000',
  rtlEnabled: false,
}

// ── Block Palette Definitions ──

export interface PaletteItem {
  type: FormBlockType | EmailBlockType
  label: string
  icon: string // lucide icon name
  category: string
}

export const FORM_PALETTE: PaletteItem[] = [
  { type: 'text', label: 'Text Input', icon: 'Type', category: 'Basic' },
  { type: 'email', label: 'Email', icon: 'Mail', category: 'Basic' },
  { type: 'phone', label: 'Phone', icon: 'Phone', category: 'Basic' },
  { type: 'number', label: 'Number', icon: 'Hash', category: 'Basic' },
  {
    type: 'textarea',
    label: 'Text Area',
    icon: 'AlignLeft',
    category: 'Basic',
  },
  {
    type: 'select',
    label: 'Dropdown',
    icon: 'ChevronDown',
    category: 'Selection',
  },
  {
    type: 'radio',
    label: 'Radio Group',
    icon: 'Circle',
    category: 'Selection',
  },
  {
    type: 'checkbox',
    label: 'Checkboxes',
    icon: 'CheckSquare',
    category: 'Selection',
  },
  {
    type: 'consent',
    label: 'Consent / Opt-in',
    icon: 'SquareCheck',
    category: 'Selection',
  },
  {
    type: 'section',
    label: 'Section',
    icon: 'LayoutList',
    category: 'Structure',
  },
  {
    type: 'columns',
    label: 'Columns',
    icon: 'Columns',
    category: 'Structure',
  },
  { type: 'subform', label: 'Subform', icon: 'Table', category: 'Structure' },
  { type: 'date', label: 'Date', icon: 'Calendar', category: 'Advanced' },
  { type: 'file', label: 'File Upload', icon: 'Upload', category: 'Advanced' },
  { type: 'url', label: 'URL', icon: 'Link', category: 'Advanced' },
  {
    type: 'hidden',
    label: 'Hidden Field',
    icon: 'EyeOff',
    category: 'Advanced',
  },
  { type: 'heading', label: 'Heading', icon: 'Heading', category: 'Layout' },
  {
    type: 'paragraph',
    label: 'Paragraph',
    icon: 'FileText',
    category: 'Layout',
  },
  { type: 'divider', label: 'Divider', icon: 'Minus', category: 'Layout' },
  { type: 'spacer', label: 'Spacer', icon: 'MoveVertical', category: 'Layout' },
  { type: 'image', label: 'Image', icon: 'Image', category: 'Layout' },
  { type: 'submit', label: 'Submit Button', icon: 'Send', category: 'Action' },
  {
    type: 'recaptcha',
    label: 'reCAPTCHA',
    icon: 'ShieldCheck',
    category: 'Advanced',
  },
]

export const EMAIL_PALETTE: PaletteItem[] = [
  { type: 'text', label: 'Text', icon: 'Type', category: 'Content' },
  { type: 'heading', label: 'Heading', icon: 'Heading', category: 'Content' },
  {
    type: 'button',
    label: 'Button',
    icon: 'MousePointer',
    category: 'Content',
  },
  {
    type: 'image_text',
    label: 'Image+Text',
    icon: 'Layout',
    category: 'Content',
  },
  {
    type: 'boxed_text',
    label: 'Boxed Text',
    icon: 'Square',
    category: 'Content',
  },
  { type: 'table', label: 'Table', icon: 'Table', category: 'Content' },
  {
    type: 'html_block',
    label: 'Insert HTML',
    icon: 'Code',
    category: 'Advanced',
  },
  { type: 'image', label: 'Image', icon: 'Image', category: 'Media' },
  { type: 'video_thumbnail', label: 'Video', icon: 'Play', category: 'Media' },
  { type: 'divider', label: 'Divider', icon: 'Minus', category: 'Layout' },
  { type: 'spacer', label: 'Spacer', icon: 'MoveVertical', category: 'Layout' },
  { type: 'columns', label: 'Columns', icon: 'Columns', category: 'Layout' },
  { type: 'social', label: 'Social Icons', icon: 'Share2', category: 'Social' },
  { type: 'footer', label: 'Footer', icon: 'Footprints', category: 'Footer' },
  {
    type: 'unsubscribe',
    label: 'Unsubscribe',
    icon: 'UserMinus',
    category: 'Footer',
  },
]

export const LANDING_PAGE_PALETTE: PaletteItem[] = [
  { type: 'heading', label: 'Heading', icon: 'Heading', category: 'Content' },
  {
    type: 'paragraph',
    label: 'Paragraph',
    icon: 'FileText',
    category: 'Content',
  },
  { type: 'image', label: 'Image', icon: 'Image', category: 'Media' },
  {
    type: 'video_embed',
    label: 'Video / GIF',
    icon: 'Play',
    category: 'Media',
  },
  { type: 'divider', label: 'Divider', icon: 'Minus', category: 'Layout' },
  { type: 'spacer', label: 'Spacer', icon: 'MoveVertical', category: 'Layout' },
  { type: 'columns', label: 'Columns', icon: 'Columns', category: 'Layout' },
  { type: 'section', label: 'Section', icon: 'LayoutList', category: 'Layout' },
  { type: 'submit', label: 'Button / CTA', icon: 'Send', category: 'Action' },
]

// ── Helpers ──

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

export function labelToName(label: string): string {
  return (
    label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/^_+|_+$/g, '') || 'field'
  )
}

export function createFormBlock(type: FormBlockType): FormBlock {
  const id = generateId()
  const defaults: Record<string, Partial<FormBlock>> = {
    text: { label: 'Text Field', placeholder: 'Enter text...' },
    email: {
      label: 'Email Address',
      placeholder: 'you@example.com',
      name: 'email',
    },
    phone: {
      label: 'Phone Number',
      placeholder: '(555) 000-0000',
      name: 'phone',
      phoneDefaultCountry: 'US',
    },
    number: { label: 'Number', placeholder: '0' },
    textarea: { label: 'Message', placeholder: 'Type your message...' },
    url: { label: 'Website URL', placeholder: 'https://' },
    select: {
      label: 'Select Option',
      validation: {
        options: [
          { label: 'Option 1', value: 'option_1' },
          { label: 'Option 2', value: 'option_2' },
        ],
      },
    },
    radio: {
      label: 'Choose One',
      validation: {
        options: [
          { label: 'Option A', value: 'a' },
          { label: 'Option B', value: 'b' },
        ],
      },
    },
    checkbox: {
      label: 'Select All That Apply',
      validation: {
        options: [
          { label: 'Choice 1', value: '1' },
          { label: 'Choice 2', value: '2' },
        ],
      },
    },
    consent: {
      label: 'Consent',
      name: 'consent',
      consentText: 'I agree to receive updates and communications',
    },
    date: { label: 'Date' },
    file: {
      label: 'Upload File',
      validation: { accept: '.pdf,.doc,.docx,.jpg,.png', maxFileSize: 5 },
    },
    hidden: { label: 'Hidden Field', name: 'hidden_field', defaultValue: '' },
    heading: { label: '', content: 'Form Heading', level: 2 },
    paragraph: { label: '', content: 'Add your description text here.' },
    divider: { label: '' },
    spacer: { label: '', height: 24 },
    image: { label: '', src: '', alt: 'Image' },
    submit: { label: '', buttonText: 'Submit', buttonAlign: 'full' },
    section: {
      label: 'Section',
      sectionTitle: 'Section Title',
      children: [],
    },
    columns: {
      label: 'Columns',
      columns: [[], []],
      columnCount: 2,
      columnGap: 16,
    },
    subform: {
      label: 'Subform',
      name: 'subform',
      subformName: 'Subform',
      subformShowName: true,
      subformColumns: [
        { label: 'Column 1', type: 'text', required: false, placeholder: '' },
        { label: 'Column 2', type: 'text', required: false, placeholder: '' },
      ],
      subformMinRows: 1,
      subformMaxRows: 10,
    },
    video_embed: {
      label: '',
      videoUrl: '',
      videoAspectRatio: '16:9',
      videoAutoplay: false,
      videoMuted: true,
      videoLoop: false,
    },
    recaptcha: {
      label: 'reCAPTCHA',
      name: 'recaptcha',
      recaptchaSiteKey: '',
      recaptchaTheme: 'light',
      recaptchaSize: 'normal',
    },
  }
  const d = defaults[type] || {}
  return {
    id,
    type,
    label: d.label ?? 'Field',
    name: d.name ?? labelToName(d.label || 'field') + '_' + id.slice(0, 4),
    placeholder: d.placeholder ?? '',
    required: false,
    helpText: '',
    defaultValue: d.defaultValue ?? '',
    width: 'full',
    validation: d.validation ?? {},
    content: d.content,
    level: d.level,
    height: d.height,
    src: d.src,
    alt: d.alt,
    buttonText: d.buttonText,
    buttonAlign: d.buttonAlign,
    consentText: d.consentText,
    sectionTitle: d.sectionTitle,
    children: d.children,
    columns: d.columns,
    columnCount: d.columnCount,
    columnGap: d.columnGap,
    subformName: d.subformName,
    subformShowName: d.subformShowName,
    subformColumns: d.subformColumns,
    subformMinRows: d.subformMinRows,
    subformMaxRows: d.subformMaxRows,
    videoUrl: d.videoUrl,
    videoAspectRatio: d.videoAspectRatio,
    videoAutoplay: d.videoAutoplay,
    videoMuted: d.videoMuted,
    videoLoop: d.videoLoop,
    recaptchaSiteKey: d.recaptchaSiteKey,
    recaptchaTheme: d.recaptchaTheme,
    recaptchaSize: d.recaptchaSize,
  }
}

export function createEmailBlock(type: EmailBlockType): EmailBlock {
  const id = generateId()
  const defaults: Record<string, Partial<EmailBlock>> = {
    text: {
      content:
        'Add your text here. You can use merge tags like {{First_Name}}.',
      fontSize: 16,
      color: '#333333',
      align: 'left',
      lineHeight: 1.6,
    },
    heading: {
      content: 'Your Heading',
      fontSize: 28,
      fontWeight: 'bold',
      color: '#111827',
      align: 'center',
      lineHeight: 1.3,
    },
    button: {
      buttonText: 'Click Here',
      buttonUrl: '#',
      buttonBgColor: '#2563eb',
      buttonTextColor: '#ffffff',
      buttonBorderRadius: 6,
      buttonWidth: 'auto',
      align: 'center',
    },
    image: { src: '', alt: 'Image', imageWidth: 100, align: 'center' },
    video_thumbnail: {
      src: '',
      alt: 'Watch Video',
      videoUrl: '',
      imageWidth: 100,
      align: 'center',
    },
    divider: {
      dividerColor: '#e5e7eb',
      dividerThickness: 1,
      dividerStyle: 'solid',
    },
    spacer: { height: 20 },
    columns: { columns: [[], []], columnRatio: '50-50' },
    social: {
      socialLinks: [
        { platform: 'facebook', url: '#' },
        { platform: 'twitter', url: '#' },
        { platform: 'instagram', url: '#' },
      ],
      socialIconSize: 32,
      align: 'center',
    },
    unsubscribe: {
      content:
        'If you no longer wish to receive these emails, you can <a href="{{unsubscribe_link}}">unsubscribe here</a>.',
      fontSize: 12,
      color: '#9ca3af',
      align: 'center',
      lineHeight: 1.5,
    },
    image_text: {
      imageTextLayout: 'image-left',
      imageTextImageSrc: '',
      imageTextImageAlt: 'Image',
      imageTextContent: 'Add your text here...',
      imageTextImageWidth: 50,
      align: 'left',
    },
    footer: {
      footerCompanyName: 'Your Company Name',
      footerAddress: '123 Main St, City, Country',
      footerLinks: [
        { label: 'Website', url: '#' },
        { label: 'Privacy Policy', url: '#' },
      ],
      fontSize: 12,
      color: '#9ca3af',
      align: 'center',
    },
    table: {
      tableRows: 2,
      tableColumns: 2,
      content:
        '<tr><td>Cell 1</td><td>Cell 2</td></tr><tr><td>Cell 3</td><td>Cell 4</td></tr>',
      tableData: {
        rows: [
          ['Cell 1', 'Cell 2'],
          ['Cell 3', 'Cell 4'],
        ],
        hasHeader: false,
        headerBgColor: '#f3f4f6',
        headerTextColor: '#111827',
        borderColor: '#e5e7eb',
        stripedRows: false,
        stripedColor: '#f9fafb',
        cellAlign: 'left',
      },
    },
    boxed_text: {
      content: 'This is a boxed text block.',
      boxedTextBorderColor: '#e5e7eb',
      boxedTextBorderRadius: 8,
      boxedTextBgColor: '#f9fafb',
      paddingTop: 20,
      paddingBottom: 20,
      paddingLeft: 20,
      paddingRight: 20,
    },
    html_block: {
      htmlContent:
        '<!-- Add your custom HTML here -->\n<div>\n  <p>Custom HTML content</p>\n</div>',
    },
  }
  const d = defaults[type] || {}
  return {
    id,
    type,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 20,
    paddingRight: 20,
    ...d,
  }
}
